/**
 * Thin API client. All requests send the session cookie (credentials: 'include').
 * In dev the Vite proxy maps /api/* to the dev API Gateway; in prod VITE_API_BASE_URL
 * is baked in (falls back to same-origin /api behind CloudFront).
 *
 * Domain-scoped model (project ADR-0006): one portfolio, scoped per user by portfolio (viewer) or
 * individual domain (owner). Admins manage the single portfolio, domain portfolios, and users.
 */
import type {DiffDomain, Role, UploadDiff} from '@ait-tracker/shared';
import {MOCK_ENABLED, mockApi} from '../dev/mock';

export type {DiffDomain, UploadDiff};

// Vite always loads .env.production for `vite build` regardless of target environment (the
// build workflow never passes a --mode), so a value baked into .env.production would be the same
// for every environment. The API's custom domain is always `api.<ui-domain>` (see
// infrastructure/config: api.domainName = "api." + ui.domainName, same dns.domainName for both),
// so derive it from the page's own hostname instead of trusting a build-time value — that is
// correct for dev/uat/prod alike with no env-specific config needed. VITE_API_BASE_URL remains a
// manual override for the rare case that doesn't hold (e.g. local testing against a fixed API).
const API_BASE = import.meta.env.DEV
  ? '/api'
  : import.meta.env.VITE_API_BASE_URL || `https://api.${window.location.hostname}`;

/**
 * Full-page URL that begins Entra SSO (spec 005). A top-level navigation (not fetch) — the browser
 * follows the backend's 302 to Microsoft and back. `returnTo` is validated server-side (FR-014).
 */
export function ssoLoginUrl(returnTo = '/dashboard'): string {
  return `${API_BASE}/auth/sso/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export interface SessionUser {
  /** Email address — the login identity. */
  email: string;
  /** Display name shown in the UI. */
  displayName: string;
  roles: Role[];
  /** Portfolio ids a portfolio-viewer may see. */
  assignedPortfolios: string[];
  /** Individual domain ids a viewer/owner may see. */
  assignedDomainIds: string[];
}

/** A domain as returned by GET /domains (admin management view). */
export interface AdminDomain {
  domainId: string;
  name: string;
  portfolio: string;
  active: boolean;
}

/** A first-class portfolio as returned by GET /portfolios (admin view; includes live domain count). */
export interface Portfolio {
  portfolioId: string;
  name: string;
  description?: string;
  domainCount: number;
}

/** A user as returned by GET /users (never includes the password hash). */
export interface ManagedUser {
  email: string;
  displayName: string;
  roles: Role[];
  assignedPortfolios: string[];
  assignedDomainIds: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Fields accepted when creating a user. Identity is the email; auth is Entra SSO (no credential). */
export interface NewUser {
  email: string;
  displayName: string;
  roles: Role[];
  assignedPortfolios?: string[];
  assignedDomainIds?: string[];
}

/** Fields accepted when updating a user (all optional — only those sent are changed). */
export interface UserUpdate {
  displayName?: string;
  roles?: Role[];
  assignedPortfolios?: string[];
  assignedDomainIds?: string[];
  enabled?: boolean;
}

/** Fields accepted when creating/updating a portfolio. */
export interface PortfolioInput {
  name?: string;
  description?: string;
}

export class ApiError extends Error {
  /** Optional field-level details (e.g. schema validation errors from an upload). */
  details?: string[];
  constructor(public status: number, message: string, details?: string[]) {
    super(message);
    this.name = 'ApiError';
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {'Content-Type': 'application/json', ...(init?.headers ?? {})},
    ...init
  });
  if (!res.ok) {
    let message = res.statusText;
    let details: string[] | undefined;
    try {
      const body = await res.json();
      message = body.error ?? message;
      if (Array.isArray(body.details)) details = body.details;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, details);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

const realApi = {
  logout: () => request<{success: boolean}>('/auth/logout', {method: 'POST'}),

  /** Current session identity — used by AuthContext to hydrate after the SSO redirect / on refresh. */
  getMe: () => request<{user: SessionUser}>('/auth/me'),

  // ── scoped read (US1/US2) ──
  /** The caller's scoped portfolio — latest by default, or a specific upload timestamp (admin). */
  getDashboard: (version?: string) =>
    request<unknown>(`/dashboard${version ? `?version=${encodeURIComponent(version)}` : ''}`),

  /** Upload version history (admin only). */
  listSnapshots: () =>
    request<{versions: {uploadedAt: string; uploadedBy: string}[]}>('/snapshots'),

  // ── admin management (US3/US4/US5) ──
  /**
   * Step 1 of upload (spec 003): stage the file and get a DIFF back without writing anything. Ask
   * the API for a presigned PUT, send the (large) file straight to S3 — bypassing the API
   * Gateway/WAF body-size limit — then call preview, which validates + diffs it against the chris-v1
   * domains and returns added/removed/renamed/unchanged (keyed by stable domain id). `file` is the
   * parsed JSON. The returned `uploadId` is passed to `commitSnapshot` to capture after confirm.
   */
  previewSnapshot: async (file: unknown): Promise<UploadDiff> => {
    const {uploadId, uploadUrl} = await request<{uploadId: string; uploadUrl: string}>(
      '/snapshots/upload-url',
      {method: 'POST'}
    );
    // Direct-to-S3 PUT: no credentials (presigned URL carries its own auth; cookies would break CORS).
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(file)
    });
    if (!put.ok) throw new ApiError(put.status, 'Failed to upload the file to storage.');
    return request<UploadDiff>('/snapshots/preview', {method: 'POST', body: JSON.stringify({uploadId})});
  },

  /**
   * Step 2 of upload (spec 003): capture a previewed staged upload. Renames need no client input —
   * identity is the stable domain id, so a renamed domain keeps its portfolio + owner grants.
   */
  commitSnapshot: (uploadId: string) =>
    request<{uploadedAt: string; uploadedBy: string; domainCount: number}>('/snapshots', {
      method: 'POST',
      body: JSON.stringify({uploadId})
    }),

  listDomains: () => request<{domains: AdminDomain[]}>('/domains'),

  setDomainPortfolio: (domainId: string, portfolio: string) =>
    request<{domainId: string; portfolio: string}>(`/domains/${encodeURIComponent(domainId)}`, {
      method: 'PATCH',
      body: JSON.stringify({portfolio})
    }),

  /** Hard-delete a domain (admin/testing convenience). */
  deleteDomain: (domainId: string) =>
    request<{domainId: string; deleted: boolean}>(`/domains/${encodeURIComponent(domainId)}`, {method: 'DELETE'}),

  // ── portfolios (first-class) ──
  listPortfolios: () => request<{portfolios: Portfolio[]}>('/portfolios'),

  createPortfolio: (input: PortfolioInput) =>
    request<Portfolio>('/portfolios', {method: 'POST', body: JSON.stringify(input)}),

  updatePortfolio: (portfolioId: string, input: PortfolioInput) =>
    request<Portfolio>(`/portfolios/${encodeURIComponent(portfolioId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input)
    }),

  deletePortfolio: (portfolioId: string) =>
    request<{portfolioId: string; rehomedDomains: number}>(`/portfolios/${encodeURIComponent(portfolioId)}`, {
      method: 'DELETE'
    }),

  /** Set exactly which domains belong to a portfolio (portfolio-side membership edit). */
  setPortfolioDomains: (portfolioId: string, domainIds: string[]) =>
    request<{portfolioId: string; domainIds: string[]}>(`/portfolios/${encodeURIComponent(portfolioId)}/domains`, {
      method: 'PUT',
      body: JSON.stringify({domainIds})
    }),

  // ── users ──
  listUsers: () => request<{users: ManagedUser[]}>('/users'),

  createUser: (user: NewUser) => request<ManagedUser>('/users', {method: 'POST', body: JSON.stringify(user)}),

  updateUser: (email: string, update: UserUpdate) =>
    request<ManagedUser>(`/users/${encodeURIComponent(email)}`, {
      method: 'PATCH',
      body: JSON.stringify(update)
    })
};

// In dev with VITE_BYPASS_AUTH=true, serve mock data instead of calling the (unbuilt) backend.
// MOCK_ENABLED folds to a constant `false` in prod builds, so this resolves to realApi and the
// mock module is tree-shaken out.
export const api = MOCK_ENABLED ? mockApi : realApi;
